import React, { useState, useRef } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import BASE_URL from "../config";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState(""); // For validation message
  const debounceRef = useRef(null);

  // Set page title
  React.useEffect(() => {
    document.title = "Softisans | Forgot Password";
  }, []);

  // Check if email exists in DB
  const checkEmailExists = async (inputEmail) => {
    if (!inputEmail) {
      setEmailError("");
      return;
    }
    // Add email format validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(inputEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }
    try {
      const res = await axios.get(
        `${BASE_URL}api/user/check-email?email=${encodeURIComponent(inputEmail)}`
      );
      if (!res.data.exists) {
        setEmailError("This email is not registered.");
      } else {
        setEmailError("");
      }
    } catch (err) {
      setEmailError("Error checking email.");
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(""); // Reset error on change

    // Debounce API call
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      checkEmailExists(value);
    }, 400);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (emailError) {
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${BASE_URL}api/user/forgot-password`, { email });
      console.log("Forgot password API success:", response);
      setEmail(""); // Clear the input after sending
      toast.success("We've sent a password reset link to your email address.");
    } catch (error) {
      console.log("Forgot password API error:", error, error?.response?.data); // Log error details for debugging
      // Show specific error if email is not registered
      if (error.response?.status === 404) {
        setEmailError("This email is not sent");
      } else {
        toast.error(
          error.response?.data?.message || "Failed to send reset link"
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100">
      <div className="container d-flex justify-content-center align-items-center flex-grow-1">
        <div
          className="login-box p-4 shadow rounded bg-white"
          style={{ width: "100%", maxWidth: "400px" }}
        >
          <div className="text-center mb-4">
            <img
              src="https://res.cloudinary.com/softisans-com/image/upload/v1592236220/Logo/Softisans-Logo.png"
              alt="Softisans Logo"
              style={{ maxWidth: "180px", width: "60%", height: "auto" }}
            />
          </div>
          <h2 className="text-center">Forgot Password</h2>
          <p className="text-center text-muted">
            Enter your email address and we'll send you a password reset link.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-group mb-3">
              <input
                type="email"
                placeholder="Email"
                className={`form-control ${
                  emailError ? "is-invalid" : ""
                }`}
                value={email}
                onChange={handleEmailChange}
                disabled={submitting}
              />
              {emailError && (
                <div
                  className="invalid-feedback"
                  style={{ display: "block" }}
                >
                  {emailError}
                </div>
              )}
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send Reset Link"}
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer /> {/* Add this line for toast notifications */}
    </div>
  );
};

export default ForgotPassword;
